import React from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { GET_REFERRER_READY } from "../../graphql/queries/care";
import { UPSERT_BOOKING_TYPE_REFERRER } from "../../graphql/mutations/care";
import { SEND_EMAIL_MUTATION } from "../../graphql/mutations";

type Props = {
  bookingTypeId?: string | number;
  referrerId?: string | number;
  groupId?: string | number | null;
  scopeKey?: string | number | null;
  forceReady?: boolean;
  onEnabled?: () => void;
  disabled?: boolean;
  disabledReason?: string | null;
  referrerEmail?: string | null;
  referrerName?: string | null;
  businessName?: string | null;
  providerName?: string | null;
  bookingLink?: string | null;
};

const ReferrerReadyToggle: React.FC<Props> = ({
  bookingTypeId,
  referrerId,
  groupId,
  scopeKey,
  forceReady = false,
  onEnabled,
  disabled = false,
  disabledReason = null,
  referrerEmail,
  referrerName,
  businessName,
  providerName,
  bookingLink,
}) => {
  const numericBT = bookingTypeId != null ? Number(bookingTypeId) : null;
  const numericRef = referrerId != null ? Number(referrerId) : null;
  const normalizedGroupId =
    groupId != null && String(groupId).trim() !== "" ? String(groupId).trim() : null;
  const normalizedScopeKey =
    scopeKey != null && String(scopeKey).trim() !== "" ? String(scopeKey).trim() : null;
  const readyStorageKey = React.useMemo(
    () =>
      numericBT && numericRef
        ? `referrer-ready:${numericBT}:${numericRef}:${normalizedGroupId || "nogroup"}:${
            normalizedScopeKey || "noscope"
          }`
        : null,
    [normalizedGroupId, normalizedScopeKey, numericBT, numericRef]
  );
  const missingBookingType = !numericBT;
  const skip = !numericBT || !numericRef;

  const { data, loading, refetch } = useQuery(GET_REFERRER_READY, {
    variables: { bookingTypeId: numericBT!, referrerId: numericRef as number },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    skip,
  });

  const readyRows = React.useMemo(
    () => (Array.isArray(data?.bookingTypeReferrers) ? data.bookingTypeReferrers : []),
    [data?.bookingTypeReferrers]
  );
  const current = React.useMemo(() => {
    if (!readyRows.length) return null;
    if (normalizedGroupId) {
      const exactReady = readyRows.find(
        (row: any) =>
          Boolean(row?.isReady) &&
          String(row?.groupId || "").trim() === normalizedGroupId
      );
      if (exactReady) return exactReady;
      const exact = readyRows.find(
        (row: any) => String(row?.groupId || "").trim() === normalizedGroupId
      );
      if (exact) return exact;
    }
    const noGroupReady = readyRows.find(
      (row: any) =>
        Boolean(row?.isReady) &&
        !String(row?.groupId || "").trim()
    );
    if (noGroupReady) return noGroupReady;
    const noGroup = readyRows.find((row: any) => !String(row?.groupId || "").trim());
    return noGroup ?? readyRows[0] ?? null;
  }, [normalizedGroupId, readyRows]);
  const isReadyInitial = React.useMemo(
    () => Boolean(current?.isReady),
    [current]
  );
  const [localReady, setLocalReady] = React.useState(false);
  React.useEffect(() => {
    setLocalReady(false);
  }, [readyStorageKey]);
  React.useEffect(() => {
    if (typeof window === "undefined" || !numericBT || !numericRef) return undefined;

    const syncReady = (event: Event) => {
      const detail = (event as CustomEvent)?.detail || {};
      if (
        Number(detail?.bookingTypeId) === numericBT &&
        Number(detail?.referrerId) === numericRef &&
        String(detail?.groupId || "").trim() === String(normalizedGroupId || "").trim() &&
        String(detail?.scopeKey || "").trim() === String(normalizedScopeKey || "").trim()
      ) {
        setLocalReady(true);
      }
    };

    window.addEventListener("plany-referrer-ready-changed", syncReady as EventListener);
    return () => {
      window.removeEventListener(
        "plany-referrer-ready-changed",
        syncReady as EventListener
      );
    };
  }, [normalizedGroupId, normalizedScopeKey, numericBT, numericRef]);
  const isReady = forceReady || localReady || isReadyInitial;

  const [upsertReady, { loading: saving }] = useMutation(
    UPSERT_BOOKING_TYPE_REFERRER
  );
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  const notifyReferrerProviderOpenForBooking = React.useCallback(async () => {
    const to = (referrerEmail || "").trim();
    if (!to) {
      console.warn("ReferrerReadyToggle: missing referrerEmail; skip notify.");
      return;
    }

    const community = (businessName || providerName || "the provider").trim();
    const subject = `Tour calendar now available at ${community}`;
    const link = bookingLink?.trim();
    const friendlyReferrer = (referrerName || "").trim() || "there";
    const providerLabel = (providerName || community).trim();

    const ctaHtml = link
      ? `
        <p style="margin:12px 0 0 0;">Use the button below to view the provider's live availability and choose a time:</p>
        <p style="margin:12px 0;">
          <a href="${link}"
             style="display:inline-block; padding:10px 16px; text-decoration:none; border-radius:8px; background:#1976d2; color:#fff;">
            Choose Tour Time
          </a>
        </p>
        <p style="margin:8px 0 0 0;">If the button doesn't work, open this link:</p>
        <p style="word-break:break-all; margin:4px 0;"><a href="${link}">${link}</a></p>
      `
      : "";

    const html = `
      <div style="font-family:Segoe UI, Helvetica, Arial, sans-serif; max-width:720px; margin:auto;">
        <h2 style="margin:0 0 10px 0;">${providerLabel} shared a tour calendar</h2>
        <p style="margin:0 0 10px 0;">Hello ${friendlyReferrer},</p>
        <p style="margin:0 0 10px 0;">
          <strong>${community}</strong> is now ready for tour scheduling from the provider's live availability.
        </p>
        ${ctaHtml}
        <hr style="border:none;border-top:1px solid #eee; margin:16px 0"/>
        <div style="font-size:12px; color:#777;">Generated by Plany365 Care</div>
      </div>
    `;

    const text = link
      ? `${providerLabel} shared a tour calendar. Choose a time at ${community}: ${link}`
      : `${providerLabel} shared a tour calendar. Choose a time at ${community}.`;

    await sendEmail({
      variables: {
        from_email: null,
        to,
        subject,
        text,
        html,
      },
    }).catch((err) => {
      console.warn("ReferrerReadyToggle: referrer notify failed:", err);
    });
  }, [
    bookingLink,
    businessName,
    providerName,
    referrerEmail,
    referrerName,
    sendEmail,
  ]);

  const handleEnable = async () => {
    if (!numericBT || !numericRef || isReady) return;

    try {
      await upsertReady({
        variables: {
          input: {
            bookingTypeId: numericBT,
            referrerId: numericRef,
            isReady: true,
            groupId: normalizedGroupId,
          },
        },
        optimisticResponse: {
          upsertBookingTypeReferrer: {
            __typename: "BookingTypeReferrer",
            id: current?.id ?? `temp-${numericBT}-${numericRef}`,
            bookingTypeId: numericBT,
            referrerId: numericRef,
            isReady: true,
            groupId: normalizedGroupId,
            updatedAt: new Date().toISOString(),
          },
        },
      });

      await refetch?.();
      setLocalReady(true);
      onEnabled?.();
      await notifyReferrerProviderOpenForBooking();
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("plany-referrer-ready-changed", {
            detail: {
              bookingTypeId: numericBT,
              referrerId: numericRef,
              groupId: normalizedGroupId,
              scopeKey: normalizedScopeKey,
            },
          })
        );
      }
    } catch (_err) {
      setLocalReady(false);
    }
  };

  if (!numericRef) return null;
  if (missingBookingType) {
    return (
      <Tooltip
        title={
          disabled
            ? disabledReason || "This action is not available yet."
            : "No tour booking type is available for this provider yet."
        }
      >
        <span>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disabled
          >
            Share Tour Booking With Referrer
          </Button>
        </span>
      </Tooltip>
    );
  }
  if (loading) return <CircularProgress size={18} />;

  return (
    <Tooltip
      title={
        disabled
          ? disabledReason || "This action is not available yet."
          :
        isReady
          ? "Tour calendar already shared. The referrer can book from the portal."
          : "Share the tour link by email and make booking available in the referrer portal."
      }
    >
      <span>
        {isReady ? (
          <Button
            size="small"
            color="success"
            variant="outlined"
            disabled
          >
            Tour Booking Shared
          </Button>
        ) : (
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={handleEnable}
            disabled={disabled || saving}
          >
            Share Tour Booking With Referrer
          </Button>
        )}
      </span>
    </Tooltip>
  );
};

export default ReferrerReadyToggle;
